import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Skeleton } from './Skeleton';

const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

const SkeletonContainer = styled.div`
  padding: 80px 24px 24px;
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const SkeletonContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr 1fr'};
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const SkeletonCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 24px;
`;

const SkeletonHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const SkeletonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  align-items: center;
`;

const SkeletonBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const SkeletonBadge = styled.div`
  background: linear-gradient(
    90deg,
    ${props => props.theme.colors.surfaceSecondary} 25%,
    ${props => props.theme.colors.hover} 37%,
    ${props => props.theme.colors.surfaceSecondary} 63%
  );
  background-size: 400% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
  height: 28px;
  width: ${props => Math.random() * 60 + 80}px;
  border-radius: ${props => props.theme.borderRadius.small};
`;

// Home Page Skeleton
export const HomeSkeleton = () => (
  <SkeletonContainer>
    <SkeletonContent>
      {/* Hero Section */}
      <SkeletonHeader>
        <Skeleton.Text height="32px" width="60%" marginBottom="16px" />
        <Skeleton.Text height="24px" width="80%" marginBottom="24px" />
        <Skeleton.Text lines={2} height="16px" marginBottom="32px" />
        <SkeletonRow style={{ justifyContent: 'center' }}>
          <Skeleton.Button width="140px" />
          <Skeleton.Button width="160px" />
        </SkeletonRow>
      </SkeletonHeader>

      {/* Stats Cards */}
      <SkeletonGrid columns="repeat(auto-fit, minmax(250px, 1fr))">
        {Array.from({ length: 4 }, (_, i) => (
          <SkeletonCard key={i}>
            <Skeleton.Avatar size="48px" style={{ margin: '0 auto 16px' }} />
            <Skeleton.Text height="24px" width="40%" style={{ margin: '0 auto 8px' }} />
            <Skeleton.Text height="14px" width="60%" style={{ margin: '0 auto' }} />
          </SkeletonCard>
        ))}
      </SkeletonGrid>

      {/* Features Section */}
      <SkeletonCard style={{ marginBottom: '40px' }}>
        <SkeletonHeader>
          <Skeleton.Text height="24px" width="30%" marginBottom="12px" />
          <Skeleton.Text height="16px" width="50%" />
        </SkeletonHeader>
        <SkeletonGrid columns="repeat(auto-fit, minmax(300px, 1fr))">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <Skeleton.Avatar size="40px" style={{ margin: '0 auto 16px' }} />
              <Skeleton.Text height="16px" width="70%" marginBottom="8px" />
              <Skeleton.Text lines={2} height="14px" />
            </div>
          ))}
        </SkeletonGrid>
      </SkeletonCard>
    </SkeletonContent>
  </SkeletonContainer>
);

// About Page Skeleton
export const AboutSkeleton = () => (
  <SkeletonContainer>
    <SkeletonContent>
      <SkeletonHeader>
        <Skeleton.Text height="24px" width="20%" marginBottom="8px" />
        <Skeleton.Text height="16px" width="60%" />
      </SkeletonHeader>

      <SkeletonGrid>
        {/* Personal Info Card */}
        <SkeletonCard>
          <Skeleton.Text height="16px" width="40%" marginBottom="16px" />
          {Array.from({ length: 3 }, (_, i) => (
            <SkeletonRow key={i}>
              <Skeleton.Avatar size="16px" />
              <div style={{ flex: 1 }}>
                <Skeleton.Text height="12px" width="30%" marginBottom="4px" />
                <Skeleton.Text height="14px" width="70%" marginBottom="0px" />
              </div>
            </SkeletonRow>
          ))}
        </SkeletonCard>

        {/* Professional Summary Card */}
        <SkeletonCard>
          <Skeleton.Text height="16px" width="50%" marginBottom="16px" />
          <Skeleton.Text lines={4} height="14px" />
        </SkeletonCard>
      </SkeletonGrid>

      {/* Skills Card */}
      <SkeletonCard style={{ marginBottom: '24px' }}>
        <Skeleton.Text height="16px" width="30%" marginBottom="16px" />
        <SkeletonGrid columns="repeat(auto-fit, minmax(280px, 1fr))">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i}>
              <Skeleton.Text height="14px" width="40%" marginBottom="12px" />
              <SkeletonBadgeRow>
                {Array.from({ length: Math.floor(Math.random() * 4) + 3 }, (_, j) => (
                  <SkeletonBadge key={j} />
                ))}
              </SkeletonBadgeRow>
            </div>
          ))}
        </SkeletonGrid>
      </SkeletonCard>

      <SkeletonGrid>
        {/* Experience Card */}
        <SkeletonCard>
          <Skeleton.Text height="16px" width="30%" marginBottom="16px" />
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} style={{ marginBottom: '20px', paddingLeft: '24px' }}>
              <Skeleton.Text height="14px" width="60%" marginBottom="4px" />
              <Skeleton.Text height="13px" width="40%" marginBottom="8px" />
              <Skeleton.Text lines={2} height="13px" />
            </div>
          ))}
        </SkeletonCard>

        {/* Education & Certifications Card */}
        <SkeletonCard>
          <Skeleton.Text height="16px" width="60%" marginBottom="16px" />
          <div style={{ marginBottom: '20px' }}>
            <Skeleton.Text height="14px" width="20%" marginBottom="12px" />
            <Skeleton.Text height="13px" width="70%" marginBottom="4px" />
            <Skeleton.Text height="12px" width="50%" marginBottom="8px" />
          </div>
          <div>
            <Skeleton.Text height="14px" width="30%" marginBottom="12px" />
            {Array.from({ length: 2 }, (_, i) => (
              <SkeletonCard key={i} style={{ padding: '12px', marginBottom: '12px' }}>
                <Skeleton.Text height="14px" width="80%" marginBottom="4px" />
                <Skeleton.Text height="12px" width="40%" />
              </SkeletonCard>
            ))}
          </div>
        </SkeletonCard>
      </SkeletonGrid>
    </SkeletonContent>
  </SkeletonContainer>
);

// Projects Page Skeleton
export const ProjectsSkeleton = () => (
  <SkeletonContainer>
    <SkeletonContent>
      <SkeletonHeader>
        <Skeleton.Text height="24px" width="20%" marginBottom="8px" />
        <Skeleton.Text height="16px" width="50%" marginBottom="24px" />
        
        {/* Search Bar */}
        <div style={{ maxWidth: '400px', margin: '0 auto' }}>
          <Skeleton.Text height="40px" width="100%" />
        </div>
      </SkeletonHeader>

      {/* Projects Grid */}
      <SkeletonGrid columns="repeat(auto-fit, minmax(350px, 1fr))">
        {Array.from({ length: 6 }, (_, i) => (
          <SkeletonCard key={i}>
            <Skeleton.Text height="18px" width="70%" marginBottom="12px" />
            <Skeleton.Text lines={3} height="14px" marginBottom="16px" />
            
            {/* Keywords */}
            <SkeletonBadgeRow>
              {Array.from({ length: Math.floor(Math.random() * 3) + 4 }, (_, j) => (
                <SkeletonBadge key={j} />
              ))}
            </SkeletonBadgeRow>
            
            {/* GitHub Link */}
            <Skeleton.Button width="100px" />
          </SkeletonCard>
        ))}
      </SkeletonGrid>
    </SkeletonContent>
  </SkeletonContainer>
);

// Contact Page Skeleton
export const ContactSkeleton = () => (
  <SkeletonContainer>
    <SkeletonContent>
      <SkeletonHeader>
        <Skeleton.Text height="24px" width="25%" marginBottom="8px" />
        <Skeleton.Text lines={2} height="16px" marginBottom="32px" />
      </SkeletonHeader>

      <SkeletonGrid>
        {/* Contact Form */}
        <SkeletonCard>
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} style={{ marginBottom: '24px' }}>
              <Skeleton.Text height="14px" width="20%" marginBottom="8px" />
              <Skeleton.Text 
                height={i === 3 ? '120px' : '40px'} 
                width="100%" 
                marginBottom="0px" 
              />
            </div>
          ))}
          <Skeleton.Button width="140px" />
        </SkeletonCard>

        {/* Contact Info */}
        <SkeletonCard>
          <Skeleton.Text height="18px" width="50%" marginBottom="24px" />
          
          {Array.from({ length: 3 }, (_, i) => (
            <SkeletonRow key={i} style={{ marginBottom: '20px' }}>
              <Skeleton.Avatar size="18px" />
              <div style={{ flex: 1 }}>
                <Skeleton.Text height="14px" width="30%" marginBottom="4px" />
                <Skeleton.Text height="14px" width="60%" marginBottom="0px" />
              </div>
            </SkeletonRow>
          ))}

          <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: `1px solid ${props => props.theme?.colors?.border || '#e0e0e0'}` }}>
            <Skeleton.Text height="14px" width="40%" marginBottom="8px" />
            <Skeleton.Text lines={2} height="13px" />
          </div>
        </SkeletonCard>
      </SkeletonGrid>
    </SkeletonContent>
  </SkeletonContainer>
);